import { useState, useEffect } from 'react';
import ImageSlider from './ImageSlider';
import { trackPageView } from '../Analytics';
import './Homepage.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Homepage() {
  const [images, setImages] = useState([]);
  const [popups, setPopups] = useState([]);
  const [popupIndex, setPopupIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/Images?type=partner`);

        if (!response.ok) {
          throw new Error('Failed to fetch images');
        }
        const data = await response.json();
        setImages(data);
      } catch (error) {
      }
    };

    fetchImages();
  }, []);

  useEffect(() => {
    trackPageView('/', 'Homepage - Ardency Construction');
  }, []);

  // The pop-up only appears when an admin has one scheduled for right now. The API
  // returns 204 when there is nothing to show, so the modal stays closed instead of
  // opening empty the way it used to. Several scheduled pop-ups become carousel slides.
  useEffect(() => {
    const fetchPopup = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/HomepagePopups/current`);
        if (response.status === 204 || !response.ok) return;

        const data = await response.json();
        const slides = (Array.isArray(data) ? data : [data]).filter((p) => p && p.imageUrl);

        if (slides.length > 0) {
          setPopups(slides);
          setPopupIndex(0);
          setShowModal(true);
        }
      } catch (error) {
        // A pop-up is decoration - never let it break the homepage
      }
    };

    fetchPopup();
  }, []);

  const closeModal = () => {
    setShowModal(false);
  };

  const handleModalBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const goToSlide = (index) => {
    if (popups.length === 0) return;
    // Wrap in both directions so prev from the first lands on the last
    setPopupIndex(((index % popups.length) + popups.length) % popups.length);
  };

  // Auto-advance, paused while the visitor is interacting via the arrows or dots
  const [popupPaused, setPopupPaused] = useState(false);

  useEffect(() => {
    if (!showModal || popups.length < 2 || popupPaused) return;

    const timer = setInterval(() => {
      setPopupIndex((prev) => (prev + 1) % popups.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [showModal, popups.length, popupPaused]);

  return (
    <div className='homepage-container'>
      <section>
        <ImageSlider />
      </section>

      {showModal && popups.length > 0 && (
        <div className="homepage-modal-overlay" onClick={handleModalBackdropClick}>
          <div
            className="homepage-modal-content homepage-modal-container"
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={() => setPopupPaused(true)}
            onMouseLeave={() => setPopupPaused(false)}
          >
            <button className="homepage-modal-close" onClick={closeModal}>
              &times;
            </button>

            <div className="homepage-modal-body">
              {popups.map((slide, index) => (
                <img
                  key={slide.id}
                  src={slide.imageUrl}
                  alt={slide.title || 'Announcement'}
                  className={`homepage-modal-image ${index === popupIndex ? 'is-active' : ''}`}
                  // Only the first slide is rendered in flow; the rest are stacked on top,
                  // so the modal keeps the height of the tallest image and does not jump
                  style={index === 0 ? undefined : { position: 'absolute', top: 0, left: 0 }}
                />
              ))}

              {popups.length > 1 && (
                <>
                  <button
                    type="button"
                    className="homepage-modal-nav prev"
                    onClick={() => goToSlide(popupIndex - 1)}
                    aria-label="Previous"
                  >
                    &#10094;
                  </button>
                  <button
                    type="button"
                    className="homepage-modal-nav next"
                    onClick={() => goToSlide(popupIndex + 1)}
                    aria-label="Next"
                  >
                    &#10095;
                  </button>

                  <div className="homepage-modal-dots">
                    {popups.map((slide, index) => (
                      <button
                        key={slide.id}
                        type="button"
                        className={`homepage-modal-dot ${index === popupIndex ? 'is-active' : ''}`}
                        onClick={() => goToSlide(index)}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <section className="homepage-about-section">
        <div className="homepage-about-content">
          <h3 className="homepage-about-heading">
            About Us
          </h3>
          <h2 className="homepage-about-subheading">
            Distributor of All Kind of Building Materials
            <br />
            <h2 className="homepage-about-subheading-2">
              <span>Efficient Distributor</span> • Your Building Materials Partner
            </h2>
          </h2>
          <p className="homepage-about-description">
            Ardency Construction & Trading Sdn. Bhd. was founded in April 2017 with a mission to provide top-tier building materials to clients in the construction sector. Backed by more than 20 years of industry expertise, our steady growth and strong financial performance underscore our dedication to excellence and ensuring customer satisfaction.
          </p>

          <a href="/about">
            <button className="homepage-about-button">
              Discover Our Story
            </button>
          </a>
        </div>

        <div className="homepage-about-image-container">
          <div className="homepage-about-image-frame">
            <img className="homepage-about-image" src="./ardency-building.jpg" alt="Company Site" />
          </div>
          <img className="homepage-about-dots" src="./aw3.svg" alt="Decorative dots" />
        </div>
      </section>

      <section className="homepage-cta-section">
        <div className="homepage-cta-content">
          <h2 className="homepage-cta-title">Ready to Start Your Next Construction Project?</h2>
          <p className="homepage-cta-text">Let's collaborate to bring your vision to life with quality materials and expert solutions.</p>
          <div className="homepage-cta-buttons">
            <a href="/contact" className="homepage-cta-button primary">Get a Quote</a>
            <a href="/catalogue" className="homepage-cta-button secondary">Explore More</a>
          </div>
        </div>
      </section>

      <section className="homepage-partners-section">
        <div className="homepage-partners-heading-container">
          <div className="homepage-partners-accent homepage-partners-accent-1"></div>
          <div className="homepage-partners-accent homepage-partners-accent-2"></div>
          <h2 className="homepage-partners-heading">Our Trusted Partners & Brands</h2>
          <p className="homepage-partners-subheading">
            We collaborate with industry-leading manufacturers and suppliers to provide you with the highest quality building materials.
          </p>
        </div>

        <div className="homepage-partners-slider-container">
          <div className="homepage-partners-grid">
            {images.concat(images).map((image, index) => (
              <div className="homepage-partner-item" key={`${image.id}-${index}`}>
                <img
                  src={`data:image/jpeg;base64,${image.imageData}`}
                  alt={`Partner ${image.id}`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Homepage;